import React, { FunctionComponent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RelationshipLogoLink } from './RelationshipLogoLink';
import { ColorsContext } from '../../context/ColorsContext';
import styles from './AboutRelationship.scss';
import AboutCampaignsLinks from '../AboutCampaignsLinks/AboutCampaignsLinks';
import { CharityExternalLinks } from '../../../types/__generated__';
import VerifiedCharity from '../VerifiedCharity/VerifiedCharity';
import { useOptimizely, usePageMeta } from '../../hooks';
import { Experiments } from '../../constants/experiments';
import {
  setUpHeapOptimizely,
  sendOptimizelyVariantToHeap,
} from '../../utils/setUpOptimizely';

interface AboutRelationshipProps {
  description?: string | null;
  landingPageUrl: string;
  logo?: string | null;
  name: string;
  parentName?: string | null;
  parentPageUrl?: string;
  inline?: boolean;
  logoVariant?: 'mini' | 'normal';
  campaignsUrls?: CharityExternalLinks | null;
}

const AboutRelationship: FunctionComponent<AboutRelationshipProps> = ({
  children,
  description,
  landingPageUrl,
  logo,
  name,
  parentName,
  parentPageUrl,
  inline,
  logoVariant = 'mini',
  campaignsUrls,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const textColor = useContext(ColorsContext)?.foregroundColor;
  const { pageType } = usePageMeta();
  const optimizely = useOptimizely();

  const decision = optimizely?.decide(
    Experiments.ConsumerDarkslide.VerifiedCharity.flagKey,
  );
  if (optimizely?.isReady) {
    setUpHeapOptimizely(optimizely);
    sendOptimizelyVariantToHeap(
      decision?.enabled,
      Experiments.ConsumerDarkslide.VerifiedCharity.flagKey,
    );
  }

  return (
    <section className={inline ? styles.aboutRelationship : ''}>
      {logo && (
        <RelationshipLogoLink
          name={name}
          pageUrl={landingPageUrl}
          qaIdentifier="owner-logo"
          itemProp="logo"
          logo={logo}
          variant={logoVariant}
        />
      )}
      <div>
        <h2 itemProp="name" className={inline ? styles.title : ''}>
          <a
            className={inline ? 'jg-h4' : 'jg-text--regular'}
            href={landingPageUrl}
            data-qa="relationship-name-link"
            style={{ color: textColor }}
          >
            {name}
          </a>
          {parentName && (
            <div className="jg-h6" data-qa="parent-details">
              <span className="jg-text--light" style={{ color: textColor }}>
                {t('common.runby', 'run by ')}
              </span>
              <a
                className="jg-text--regular"
                href={parentPageUrl}
                style={{ color: textColor }}
              >
                {parentName}
              </a>
            </div>
          )}
        </h2>
        {decision?.enabled &&
          (pageType === 'fundraising' || pageType === 'one_page') && (
            <VerifiedCharity />
          )}
        {description && (
          <p
            itemProp="description"
            className={inline ? 'jg-space-mbmd' : 'jg-text--large'}
            style={{ color: textColor }}
          >
            {description}
          </p>
        )}
        {children}
      </div>
      {campaignsUrls && (
        <AboutCampaignsLinks
          links={campaignsUrls.links}
          privacyUrl={campaignsUrls.privacyUrl}
          termsUrl={campaignsUrls.termsUrl}
        />
      )}
    </section>
  );
};

export default AboutRelationship;
